<template>
	<div
		class="split-container"
		ref="container"
		:class="{ vertical: isVertical, horizontal: !isVertical }"
	>
		<div class="pane pane1" :style="pane1Style">
			<slot name="first"></slot>
		</div>
		<div
			class="divider"
			:class="{ vertical: isVertical, horizontal: !isVertical }"
			@mousedown="startDrag($event)"
			@touchstart.prevent="startDrag($event)"
			@dblclick="toggleDirection"
		/>
		<div class="pane pane2" :style="pane2Style">
			<slot name="second"></slot>
		</div>
		<!-- Overlay to capture mouse events during dragging -->
		<div
			v-if="dragging"
			class="drag-overlay"
			@mousemove="onDrag"
			@mouseup="stopDrag"
			@mouseleave="stopDrag"
		/>
	</div>
</template>

<script>
export default {
	name: "SplitView",
	props: {
		// Optional prop to customize the localStorage key
		storageKey: {
			type: String,
			default: 'splitview-config'
		},
		// Props for default values if no localStorage data exists
		defaultVertical: {
			type: Boolean,
			default: false
		},
		defaultPosition: {
			type: Number,
			default: 50
		}
	},
	data() {
		return {
			isVertical: this.defaultVertical,
			position: this.defaultPosition,
			dragging: false,
			previousUserSelect: "",
			previousCursor: ""
		};
	},
	computed: {
		pane1Style() {
			return this.isVertical
				? { width: `calc(${this.position}% - 5px)` }
				: { height: `calc(${this.position}% - 5px)` };
		},
		pane2Style() {
			return this.isVertical
				? { width: `calc(${100 - this.position}% - 5px)` }
				: { height: `calc(${100 - this.position}% - 5px)` };
		}
	},
	mounted() {
		this.loadFromStorage();
	},
	methods: {
		loadFromStorage() {
			try {
				const stored = localStorage.getItem(this.storageKey);
				if (stored) {
					const config = JSON.parse(stored);
					if (typeof config.isVertical === 'boolean') {
						this.isVertical = config.isVertical;
					}
					if (typeof config.position === 'number' && config.position >= 5 && config.position <= 95) {
						this.position = config.position;
					}
				}
			} catch (error) {
				console.warn('Failed to load SplitView configuration from localStorage:', error);
			}
		},

		saveToStorage() {
			try {
				const config = {
					isVertical: this.isVertical,
					position: this.position
				};
				localStorage.setItem(this.storageKey, JSON.stringify(config));
			} catch (error) {
				console.warn('Failed to save SplitView configuration to localStorage:', error);
			}
		},

		startDrag(event) {
			if(event.detail === 2) return;
			this.dragging = true;
			// Save current user-select and cursor
			this.previousUserSelect = document.documentElement.style.userSelect;
			this.previousCursor = document.documentElement.style.cursor;
			// Disable text selection globally
			document.documentElement.style.userSelect = "none";
			// Force cursor globally (applies even outside window)
			document.documentElement.style.cursor = this.isVertical ? "col-resize" : "row-resize";

			// Disable pointer events on iframes to prevent them from capturing mouse events
			this.disableIframePointerEvents();

			// Keep window event listeners as backup
			window.addEventListener("mousemove", this.onDrag);
			window.addEventListener("mouseup", this.stopDrag);
			window.addEventListener("touchmove", this.onDrag, { passive: false });
			window.addEventListener("touchend", this.stopDrag);
			window.addEventListener("mouseleave", this.stopDrag);
		},

		stopDrag() {
			this.dragging = false;
			// Restore previous styles
			document.documentElement.style.userSelect = this.previousUserSelect || "";
			document.documentElement.style.cursor = this.previousCursor || "";

			// Re-enable pointer events on iframes
			this.enableIframePointerEvents();

			window.removeEventListener("mousemove", this.onDrag);
			window.removeEventListener("mouseup", this.stopDrag);
			window.removeEventListener("touchmove", this.onDrag);
			window.removeEventListener("touchend", this.stopDrag);
			window.removeEventListener("mouseleave", this.stopDrag);

			// Save to localStorage when drag ends
			this.saveToStorage();
		},

		onDrag(e) {
			if (!this.dragging) return;
			e.preventDefault(); // Prevent any default behavior

			const rect = this.$refs.container.getBoundingClientRect();
			let clientX, clientY;
			if (e.touches && e.touches.length > 0) {
				clientX = e.touches[0].clientX;
				clientY = e.touches[0].clientY;
			} else {
				clientX = e.clientX;
				clientY = e.clientY;
			}
			if (this.isVertical) {
				const offsetX = clientX - rect.left;
				this.position = (offsetX / rect.width) * 100;
			} else {
				const offsetY = clientY - rect.top;
				this.position = (offsetY / rect.height) * 100;
			}
			if (this.position < 5) this.position = 5;
			if (this.position > 95) this.position = 95;
		},

		toggleDirection() {
			this.isVertical = !this.isVertical;
			// Save to localStorage when direction changes
			this.saveToStorage();
		},

		// Helper methods to handle iframe pointer events
		disableIframePointerEvents() {
			const iframes = this.$refs.container.querySelectorAll('iframe');
			iframes.forEach(iframe => {
				iframe.style.pointerEvents = 'none';
			});
		},

		enableIframePointerEvents() {
			const iframes = this.$refs.container.querySelectorAll('iframe');
			iframes.forEach(iframe => {
				iframe.style.pointerEvents = 'auto';
			});
		},

		// Handle visibility changes (tab switching, etc.)
		handleVisibilityChange() {
			// If the page becomes hidden while dragging, stop the drag operation
			if (document.hidden && this.dragging) {
				this.stopDrag();
			}
		},

		// Pointer Lock API methods for capturing mouse outside browser
		requestPointerLock() {
			// Only request if supported and if the container element is available
			if (this.$refs.container && this.$refs.container.requestPointerLock) {
				// Request pointer lock (modern browsers)
				this.$refs.container.requestPointerLock();
			} else if (this.$refs.container && this.$refs.container.mozRequestPointerLock) {
				// Firefox
				this.$refs.container.mozRequestPointerLock();
			} else if (this.$refs.container && this.$refs.container.webkitRequestPointerLock) {
				// Webkit browsers
				this.$refs.container.webkitRequestPointerLock();
			}
			// Note: Pointer lock might not be granted immediately or at all
			// The drag will still work without it, just not outside the browser
		},

		exitPointerLock() {
			if (document.exitPointerLock) {
				document.exitPointerLock();
			} else if (document.mozExitPointerLock) {
				document.mozExitPointerLock();
			} else if (document.webkitExitPointerLock) {
				document.webkitExitPointerLock();
			}
		},

		// Public method to clear stored configuration
		clearStoredConfig() {
			try {
				localStorage.removeItem(this.storageKey);
				// Reset to default values
				this.isVertical = this.defaultVertical;
				this.position = this.defaultPosition;
			} catch (error) {
				console.warn('Failed to clear SplitView configuration from localStorage:', error);
			}
		}
	}
};
</script>

<style lang="scss">
@import '../plugins/variable.scss';
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

.split-container {
	display: flex;
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
}
.split-container.vertical {
	flex-direction: row;
}
.split-container.horizontal {
	flex-direction: column;
}
.pane {
	container-type: inline-size; // enables container queries
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

@each $breakpoint, $value in $grid-breakpoints {
	// Skip xs because we usually don't need a max-width for 0
	@if $breakpoint != xs {
		// max-width query: stack vertically below this breakpoint
		@container (max-width: #{breakpoint-max($breakpoint)}) {
			.pane .col-#{$breakpoint} {
				flex: 0 0 100%;
				max-width: 100%;
			}
		}

		// min-width query: side-by-side at this breakpoint and above
		@container (min-width: #{breakpoint-min($breakpoint)}) {
			.pane .col-#{$breakpoint} {
				flex: 0 0 50%;   // adjust as needed
				max-width: 50%;
			}
		}

	}
}

.divider {
	background: $gray-800;
	z-index: 10;
	flex-shrink: 0;
	touch-action: none; /* Prevents scroll during drag */
	user-select: none;
}
.divider:hover {
	background-color: $green;
}
.divider.vertical {
	width: 1em;
	cursor: col-resize;
}
.divider.horizontal {
	height: 1em;
	cursor: row-resize;
}

/* Overlay to capture mouse events during dragging */
.drag-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	cursor: inherit;
	background: transparent;
}
</style>
